import {
    CORE_8_PORTFOLIO,
    PortfolioCalculator,
    RetirementAccountType,
    SocialSecurityBenefitCalculator,
} from '@fruitful/financial-calculators';
import { roundToTwoDecimals } from '@fruitful/fruitils';
import React from 'react';
import {
    type Control,
    type FieldErrors,
    type UseFormRegister,
    type UseFormSetValue,
    type UseFormWatch,
} from 'react-hook-form';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckboxWithLabel,
    ControlledInput,
    ControlledPicker,
    H5Emphasized,
    H6Emphasized,
    IconButton,
    RotateCcwIcon,
    TrashIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { MutationErrorToastOptions } from '../../common/components/Toasts';
import type { RetirementCalculatorFormData } from '../types';

interface RetirementCalculatorMemberInputsProps {
    memberPath: 'primaryMember' | 'partnerMember';
    title: string;
    control: Control<RetirementCalculatorFormData>;
    register: UseFormRegister<RetirementCalculatorFormData>;
    errors: FieldErrors<RetirementCalculatorFormData>;
    watch: UseFormWatch<RetirementCalculatorFormData>;
    setValue: UseFormSetValue<RetirementCalculatorFormData>;
}

const getDefaultEquitiesPercentage = (): string => {
    const allocations =
        PortfolioCalculator.calculateAllocations(CORE_8_PORTFOLIO);
    return roundToTwoDecimals(allocations.equities * 100).toString();
};

const ACCOUNT_TYPE_OPTIONS = [
    { label: 'Employer 401(k)', value: RetirementAccountType.EMPLOYER_401K },
    { label: 'Roth 401(k)', value: RetirementAccountType.ROTH_401K },
    { label: 'SEP IRA', value: RetirementAccountType.SEP_IRA },
    { label: 'Solo 401(k)', value: RetirementAccountType.SOLO_401K },
    { label: 'Roth IRA', value: RetirementAccountType.ROTH_IRA },
    { label: 'Traditional IRA', value: RetirementAccountType.TRAD_IRA },
    {
        label: 'Backdoor Roth IRA',
        value: RetirementAccountType.BACKDOOR_ROTH_IRA,
    },
    {
        label: 'Taxable Brokerage',
        value: RetirementAccountType.TAXABLE_BROKERAGE,
    },
];

export const RetirementCalculatorMemberInputs = ({
    memberPath,
    title,
    control,
    register,
    errors,
    watch,
    setValue,
}: RetirementCalculatorMemberInputsProps) => {
    const accounts = watch(`${memberPath}.accounts`) ?? [];
    const toast = useToastController();

    const calculateSocialSecurityBenefit = () => {
        const formData = watch();
        const memberData = formData[memberPath];

        if (
            memberData?.currentGrossAnnualIncome != null &&
            memberData?.currentAge != null
        ) {
            const currentGrossAnnualIncome = parseFloat(
                memberData.currentGrossAnnualIncome,
            );
            const currentAge = parseInt(memberData.currentAge);
            const socialSecurityBenefitAge = parseInt(
                memberData.socialSecurityBenefitAge ?? '67',
            );

            try {
                const ssCalculator = new SocialSecurityBenefitCalculator(
                    currentAge,
                    socialSecurityBenefitAge,
                    currentGrossAnnualIncome,
                );
                const calculatedBenefit = ssCalculator.calculate();

                // Set the calculated benefit in the form
                setValue(
                    `${memberPath}.annualSocialSecurityBenefit`,
                    calculatedBenefit.toString(),
                );
            } catch (error) {
                toast.show(
                    'Error calculating social security benefit',
                    MutationErrorToastOptions(error),
                );
            }
        } else {
            toast.show(
                'Please fill in required fields',
                MutationErrorToastOptions(
                    'Current gross annual income and current age are required',
                ),
            );
        }
    };

    const addAccount = () => {
        const newAccount = {
            id: `account-${Date.now()}`,
            type: RetirementAccountType.EMPLOYER_401K,
            balance: '0',
            equitiesPercentage: getDefaultEquitiesPercentage(), // %
            costBasis: '0', // Default to 0
        };

        setValue(`${memberPath}.accounts`, [...accounts, newAccount]);
    };

    const removeAccount = (index: number) => {
        const updatedAccounts = accounts.filter((_, i) => i !== index);
        setValue(`${memberPath}.accounts`, updatedAccounts);
    };

    return (
        <YStack gap={16}>
            <H5Emphasized>{title}</H5Emphasized>

            <XStack gap={12}>
                <ControlledInput
                    label="Current Age"
                    name={`${memberPath}.currentAge`}
                    control={control}
                    register={register}
                    errors={errors}
                    type="numeric"
                    containerStyle={{
                        flex: 1,
                    }}
                />
                <ControlledInput
                    label="Retirement Age"
                    name={`${memberPath}.retirementAge`}
                    control={control}
                    register={register}
                    errors={errors}
                    type="numeric"
                    containerStyle={{
                        flex: 1,
                    }}
                />
            </XStack>

            <XStack gap={12}>
                <ControlledInput
                    label="Social Security Age"
                    name={`${memberPath}.socialSecurityBenefitAge`}
                    control={control}
                    register={register}
                    errors={errors}
                    type="numeric"
                    containerStyle={{
                        flex: 1,
                    }}
                />
                <ControlledInput
                    label="Life Expectancy"
                    name={`${memberPath}.lifeExpectancy`}
                    control={control}
                    register={register}
                    errors={errors}
                    type="numeric"
                    containerStyle={{
                        flex: 1,
                    }}
                />
            </XStack>

            <ControlledInput
                label="Current Gross Annual Income ($)"
                name={`${memberPath}.currentGrossAnnualIncome`}
                control={control}
                register={register}
                errors={errors}
                type="numeric"
            />

            <XStack gap={12}>
                <ControlledInput
                    label="Employer Match Percentage (%)"
                    name={`${memberPath}.employerMatchPercentage`}
                    control={control}
                    register={register}
                    errors={errors}
                    type="numeric"
                    containerStyle={{
                        flex: 1,
                    }}
                />
                <ControlledInput
                    label="Employer Match Max % of Income (%)"
                    name={`${memberPath}.employerMatchMaximumPercentOfIncome`}
                    control={control}
                    register={register}
                    errors={errors}
                    type="numeric"
                    containerStyle={{
                        flex: 1,
                    }}
                />
            </XStack>

            <XStack gap={8} alignItems="center">
                <ControlledInput
                    label="Annual Social Security Benefit ($)"
                    name={`${memberPath}.annualSocialSecurityBenefit`}
                    control={control}
                    register={register}
                    errors={errors}
                    type="numeric"
                    containerStyle={{ flex: 1 }}
                />
                <IconButton
                    variant="secondary"
                    icon={RotateCcwIcon}
                    onPress={calculateSocialSecurityBenefit}
                />
            </XStack>

            <YStack gap={16}>
                <CheckboxWithLabel
                    id="include-retirement-health-care-costs"
                    label="Include Retirement Healthcare Costs?"
                    checked={watch(
                        `${memberPath}.includeRetirementHealthcareCosts`,
                    )}
                    onPress={() => {
                        const currentValue = watch(
                            `${memberPath}.includeRetirementHealthcareCosts`,
                        );
                        setValue(
                            `${memberPath}.includeRetirementHealthcareCosts`,
                            !currentValue,
                        );
                    }}
                />

                <XStack gap={12}>
                    <ControlledInput
                        label="Retirement Health Care Cost ($)"
                        name={`${memberPath}.retirementHealthcareCost`}
                        control={control}
                        register={register}
                        errors={errors}
                        type="numeric"
                        disabled={true}
                        containerStyle={{
                            flex: 1,
                        }}
                    />
                    <ControlledInput
                        label="Long Term Care Cost ($)"
                        name={`${memberPath}.longTermCareCost`}
                        control={control}
                        register={register}
                        errors={errors}
                        type="numeric"
                        disabled={true}
                        containerStyle={{
                            flex: 1,
                        }}
                    />
                </XStack>
            </YStack>

            <YStack gap={12}>
                <H6Emphasized>Retirement Accounts</H6Emphasized>
                {Array.isArray(accounts) &&
                    accounts.map((account, index: number) => (
                        <YStack
                            key={index}
                            gap={12}
                            padding={12}
                            backgroundColor="$gray100"
                            borderRadius={8}
                        >
                            <XStack
                                gap={8}
                                alignItems="flex-start"
                                justifyContent="space-between"
                            >
                                <BodyMediumRegular color="$gray1000">
                                    Account ID: {account.id}
                                </BodyMediumRegular>
                                <IconButton
                                    variant="secondary"
                                    icon={TrashIcon}
                                    onPress={() => {
                                        removeAccount(index);
                                    }}
                                    tone="danger"
                                />
                            </XStack>
                            <ControlledPicker
                                label="Account Type"
                                name={`${memberPath}.accounts.${index}.type`}
                                control={control}
                                register={register}
                                errors={errors}
                                options={ACCOUNT_TYPE_OPTIONS}
                            />
                            <XStack gap={8}>
                                <ControlledInput
                                    label="Balance ($)"
                                    name={`${memberPath}.accounts.${index}.balance`}
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    type="numeric"
                                    containerStyle={{
                                        flex: 1,
                                    }}
                                />
                                <ControlledInput
                                    label="Equities (%)"
                                    name={`${memberPath}.accounts.${index}.equitiesPercentage`}
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    type="numeric"
                                    containerStyle={{
                                        flex: 1,
                                    }}
                                />
                            </XStack>
                            <ControlledInput
                                label="Cost Basis ($)"
                                name={`${memberPath}.accounts.${index}.costBasis`}
                                control={control}
                                register={register}
                                errors={errors}
                                type="numeric"
                            />
                        </YStack>
                    ))}
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    text="Add Account"
                    onPress={addAccount}
                />
            </YStack>
        </YStack>
    );
};
